import { useCallback, useEffect, useRef, useState } from 'react';
import Reveal from '../../common/Reveal';
import HomeCarouselRow from '../shared/HomeCarouselRow';
import ExpertCard from './ExpertCard';

function NavButton({ active, onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!active}
      aria-label={label}
      aria-disabled={!active}
      className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition ${
        active
          ? 'border-[#9369CC] bg-[#9369CC]/15 text-[#9369CC] shadow-md hover:bg-[#9369CC]/25'
          : 'cursor-default border-zinc-600/40 text-zinc-600/40 opacity-50'
      }`}
    >
      {children}
    </button>
  );
}

function ExpertsSection({ instructors, loading, error }) {
  const scrollRef = useRef(null);
  const list = instructors || [];
  const showNav = list.length > 1;
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    setCanScrollPrev(scrollLeft > 4);
    setCanScrollNext(scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    if (!showNav || loading) return undefined;
    updateScrollState();
    const t = window.setTimeout(updateScrollState, 100);
    window.addEventListener('resize', updateScrollState);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [showNav, loading, list, updateScrollState]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    if (direction === 'prev' && !canScrollPrev) return;
    if (direction === 'next' && !canScrollNext) return;
    el.scrollBy({
      left: direction === 'next' ? el.clientWidth : -el.clientWidth,
      behavior: 'smooth',
    });
  };

  const renderContent = () => {
    if (loading && !list.length) {
      return (
        <div className="flex items-end justify-start gap-4 overflow-hidden sm:justify-center">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className="h-[300px] w-[205px] shrink-0 animate-pulse rounded-[115px] bg-white/10 sm:h-[340px] sm:w-[230px] sm:rounded-[130px] lg:h-[380px] lg:w-[260px] lg:rounded-[145px] xl:h-[410px] xl:w-[285px] xl:rounded-[160px]"
            />
          ))}
        </div>
      );
    }

    if (error) {
      return <p className="text-center text-sm text-red-400">{error}</p>;
    }

    if (!list.length) {
      return <p className="text-center text-sm text-white/60">No experts available right now.</p>;
    }

    return (
      <HomeCarouselRow
        scrollRef={scrollRef}
        onScroll={updateScrollState}
        items={list}
        layout="compact"
        renderItem={(expert) => <ExpertCard expert={expert} />}
      />
    );
  };

  return (
    <section className="bg-zinc-900 px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
      <div className="mx-auto max-w-7xl">
        <div className="relative text-center">
          <Reveal variant="up">
            <h2 className="text-2xl font-semibold text-white sm:text-3xl lg:text-[32px]">Meet Our Experts</h2>
          </Reveal>
          <Reveal variant="up" delay={90}>
            <p className="mx-auto mt-3 max-w-2xl text-sm font-medium leading-relaxed text-[#CCCCCC] sm:text-base lg:text-lg xl:text-[24px]">
              Learn smart with mentors who understand CHSE exams and guide you at every stage of
              preparation.
            </p>
          </Reveal>
          {showNav && (
            <div className="mt-4 flex items-center justify-center gap-2 lg:absolute lg:right-0 lg:top-0 lg:mt-0 xl:right-20 xl:top-10">
              <NavButton
                active={canScrollPrev}
                onClick={() => scroll('prev')}
                label="Previous experts"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </NavButton>
              <NavButton active={canScrollNext} onClick={() => scroll('next')} label="Next experts">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </NavButton>
            </div>
          )}
        </div>

        <div className="mx-auto mt-8 w-full min-w-0 max-w-[1188px] sm:mt-10">{renderContent()}</div>
      </div>
    </section>
  );
}

export default ExpertsSection;
